@use "app/styles/sticky" as sticky;
@use "app/styles/colors" as colors;

main {
  flex-direction: row;
}

@media (min-width: 48rem) and (min-height: 38rem) {
  :global(#site-menu) {
    display: flex;
  }
}
@media (min-width: 64rem) and (min-height: 38rem) {
  :global(#page-menu) {
    display: flex;
  }
}

#pageNav {
  & ul ul {
    padding-left: 1rem;
  }
}

.article {
  // Focused by the skip link, which should not leave a ring on a whole page.
  &:focus {
    outline: none;
  }

  flex: 1;
  padding: 2.5rem 0;
  line-height: 1.5;
  min-width: 0;
  min-height: 100dvh;

  @include colors.section("red");

  $i: 1;
  @each $color in "blue", "green", "yellow" {
    & > section:nth-of-type(4n + #{$i}) {
      @include colors.section($color);
    }
    $i: $i + 1;
  }

  section {
    scroll-margin-top: sticky.$size;
    & > :last-child {
      margin-bottom: 0;
    }
  }

  section:target > :first-child {
    border-bottom: 2px solid var(--section-color-dim);
  }

  h1 {
    margin-bottom: 1rem;
    line-height: 1.2em;
  }

  h2 {
    margin-top: 1.5em;
  }

  h2,
  h3,
  h4,
  h5,
  h6 {
    margin-top: 1em;
    margin-bottom: 1rem;
  }

  h1,
  h2,
  h3,
  h4,
  h5,
  h6 {
    color: var(--section-color);
    background: linear-gradient(
      to bottom,
      var(--section-color),
      color-mix(in srgb, var(--section-color-alt), var(--section-color))
    );
    background-clip: text;
    -webkit-text-fill-color: transparent;

    code {
      background: inherit;
      background-clip: text;
      -webkit-text-fill-color: transparent;
      position: relative;
      padding-left: 0.75ch;
      padding-right: 0.75ch;

      &::before {
        content: "";
        position: absolute;
        background-color: var(--section-color-alt-dim);
        inset: 0;
        border-radius: 1em;
        z-index: -1;
      }
      &::after {
        content: "";
      }
    }

    > a {
      color: var(--section-color);

      &:hover {
        color: var(--section-color);
        text-decoration: none;

        &::before {
          color: var(--section-color);
          -webkit-text-fill-color: var(--section-color);
        }
      }

      &::before {
        @media (min-width: 48rem) {
          position: absolute;
          display: inline-flex;
          content: "#";
          text-wrap: nowrap;
          padding-right: 0.5rem;
          color: var(--section-color-dim);
          -webkit-text-fill-color: var(--section-color-dim);
          scale: 0.75;
          top: 50%;
          left: 0;
          translate: -100% -50%;
        }
      }
    }
  }

  p {
    margin-bottom: 1.3rem;
  }

  ul,
  ol {
    margin-bottom: 1.3rem;
    padding-left: 2rem;

    ul,
    ol {
      margin-bottom: 0;
    }
  }

  li::marker {
    color: var(--section-color-alt);
    font-size: 1.2rem;
  }

  pre,
  code {
    margin: 2rem 0;
  }

  hr {
    border: 1px solid var(--section-color-dim);
    margin: 3rem 0;
  }

  :global(.table-scroll) {
    margin: 2rem 0;
    border-radius: 1rem;
    overflow-x: auto;
  }

  table {
    border-collapse: collapse;
    display: table;
    width: 100%;

    thead {
      border-bottom: 1px solid var(--section-color);
    }

    th {
      font-weight: 500;

      &:not([align]) {
        text-align: start;
      }
    }

    thead,
    tr:nth-child(even) {
      background-image: linear-gradient(
        to bottom left,
        color-mix(
          in srgb,
          var(--section-color-dim) 24%,
          var(--color-background)
        ),
        color-mix(in srgb, var(--section-color-dim) 8%, var(--color-background))
      );
    }

    th,
    td {
      padding: 1rem 1.25rem;
    }
  }
}

#contributors {
  ul {
    display: flex;
    list-style-type: none;
    flex-wrap: wrap;
    gap: 1rem;
    padding: 0;

    & > li > a {
      display: flex;
      flex-direction: column;
      justify-content: center;
      align-items: center;
      text-decoration: none;
      color: var(--color-foreground);
      font-size: x-small;

      & > :first-child {
        width: 5rem;
        height: 5rem;
        border-radius: 50%;
        border: 2px solid var(--section-color);
        display: flex;
        justify-content: center;
        align-items: center;

        svg {
          height: 2rem;

          path {
            fill: var(--section-color);
          }
        }
      }
    }
  }
}

:global {
  .marko-codespan__tag {
    &::before {
      content: "<";
    }

    &::after {
      content: ">";
    }
  }

  .marko-codespan__attribute::after {
    content: "=";
  }

  #syntax-legend pre {
    border: none;
    font-size: max(0.8rem, min(4dvw, 1.5rem));
    background-color: var(--section-color-dim);
    border-radius: 0.5rem;
    padding: 1.5rem;
    width: 100%;
    max-width: 100% !important;
  }

  callout {
    display: block;
    position: relative;
    margin: 2rem 0;
    border: 1px solid var(--section-color);
    background-image: linear-gradient(
      to bottom left,
      color-mix(in srgb, var(--section-color-dim) 24%, var(--color-background)),
      color-mix(in srgb, var(--section-color-dim) 8%, var(--color-background))
    );
    padding: 1rem;
    padding-bottom: 0;
    border-radius: 0.5rem;
    min-height: 9rem;
    &::after {
      position: absolute;
      z-index: -1;
      inset: 0;
      transform-origin: bottom;
      transform: scale(0.9, 0.7);
      content: "";
      background-color: var(--section-color-dim-alt);
      filter: blur(50px);
      opacity: 0.2;
    }
    @media (min-width: 48rem) {
      padding: 1.5rem;
      padding-bottom: 1rem;
    }

    pre,
    code {
      margin: 1.3rem 0;
    }
  }

  callout > cot {
    color: var(--section-color);
    display: flex;
    gap: 0.5rem;
    align-items: center;
    text-transform: uppercase;
    font-weight: 700;
    margin-bottom: 0.7rem;

    svg {
      fill: currentColor;
    }
  }

  $admonition-types: (
    ("note", "doc", "blue"),
    ("caution", "throw", "red"),
    ("tip", "link", "yellow"),
    ("important", "button", "green"),
    ("warning", "crash", "gray"),
    ("tldr", "pixel", "yellow")
  );

  callout::before {
    pointer-events: none;
    float: right;
    content: "";
    margin-top: -3.6rem;
    margin-right: -1.6rem;
    width: 8rem;
    height: 8rem;
    background-image: var(--widget-image);
    background-repeat: no-repeat;
  }

  .admonition-note::before {
    rotate: 4deg;
  }
  .admonition-caution::before {
    rotate: -6deg;
  }
  .admonition-tip::before {
    scale: -1 1;
    rotate: -5deg;
  }
  .admonition-important::before {
    scale: -1 1;
    rotate: 3deg;
  }

  @each $name, $widget, $color in $admonition-types {
    .admonition-#{$name} {
      @include colors.section($color, true);
      &::before {
        --widget-image: url("/assets/widget/#{$widget}.svg");
      }
    }
  }

  @media (prefers-color-scheme: dark) {
    @each $name, $widget, $color in $admonition-types {
      .admonition-#{$name}::before {
        --widget-image: url("/assets/widget/#{$widget}.svg#dark");
      }
    }
  }

  @media (min-width: 48rem) and (min-height: 38rem) {
    callout::before {
      width: 7.5rem;
      height: 11.25rem;
      margin-top: -4rem;
      margin-left: -1.5rem;
      margin-right: -1rem;
      rotate: 0deg;
    }

    @each $name, $widget in $admonition-types {
      .admonition-#{$name}::before {
        --widget-image: url("/assets/widget/#{$widget}-legs.svg");
      }
    }

    @media (prefers-color-scheme: dark) {
      @each $name, $widget, $color in $admonition-types {
        .admonition-#{$name}::before {
          --widget-image: url("/assets/widget/#{$widget}-legs.svg#dark");
        }
      }
    }
  }

  #next-steps,
  #further-reading {
    ul {
      display: grid;
      list-style: none;
      gap: 2rem;
      margin: 0;
      padding: 0;
      grid-template-columns: repeat(2, minmax(0, 1fr));

      a {
        display: flex;
        align-items: center;
        padding: 1rem;
        width: 100%;
        height: 5rem;
        border: 1px solid;
        border-radius: 1rem;
        text-decoration: none;
        transition: box-shadow 0.3s cubic-bezier(0.4, 0, 0.2, 1);
      }

      a:hover {
        box-shadow: var(--section-color) 0px 2px 4px -1px;
      }
    }
  }
}
